import classNames from 'classnames';
import React from 'react';

import {InDemandCareerWrapper, InDemandCareer} from '@udemy/career-academies';
import {AboveTheFoldProvider} from '@udemy/design-system-utils';
import {useI18n} from '@udemy/i18n';
import {LearningTestimonialCard} from '@udemy/react-discovery-units';
import {ContentArea, ContentGrid, ContentGridItem} from '@udemy/react-layout-components';
import {useConsumerSubscriptionPlan} from '@udemy/subscription-graphql';

import {useVerifyIsUsMarketplace} from '../../hooks/useVerifyIsUsMarketplace';
import {FrequentlyAskedQuestions} from '../career-academy-landing-page/components/academies-faq/academies-faq.react-component';
import {AdditionalResources} from '../career-academy-landing-page/components/additional-resources/additional-resources.react-component';
import {Disclaimer} from '../career-academy-landing-page/components/disclaimer/disclaimer.react-component';
import styles from './career-academy-overview-page.module.less';
import {articles} from './data/articles';
import {OVERVIEW_TESTIMONIALS} from './data/testimonials';

export interface CareerAcademyOverviewPageProps {
    academies: InDemandCareer[];
    locale: string;
}

export function CareerAcademyOverviewPage({academies, locale}: CareerAcademyOverviewPageProps) {
    const {gettext} = useI18n();
    const personalPlanContext = useConsumerSubscriptionPlan();
    const {isUSMarketplace} = useVerifyIsUsMarketplace(locale);

    const OVERVIEW_FAQS = [
        {
            question: gettext('Who are Career Accelerators for?'),
            answer: gettext(
                'Learners interested in gaining fundamental and practical skills that employers are looking for, looking to fast track learning for an entry-level career.',
            ),
        },
        {
            question: gettext('Is any prior knowledge or experience required?'),
            answer: gettext(
                'No prior knowledge is required. The program is designed to be accessible to learners of all backgrounds, regardless of previous experience.',
            ),
        },
        {
            question: gettext('How are courses selected for career accelerators?'),
            answer: gettext(
                'Courses are carefully curated by Udemy’s content experts using data-driven insights from our global learner base of over 62 million. We prioritize top-rated, fresh, and relevant courses for professional learners.',
            ),
        },
    ];

    return (
        <div>
            <AboveTheFoldProvider>
                <div className={classNames('ud-container', styles['heading'])}>
                    <h1 className={styles['main-heading']}>
                        {gettext('Ready to reimagine your career?')}
                    </h1>
                    <div className={classNames('ud-text-lg', styles['subheading'])}>
                        {personalPlanContext && 'id' in personalPlanContext
                            ? gettext(
                                  'Join Career Accelerators through Udemy Personal Plan and get the structure, skills, and real-world experience that makes you an exceptional candidate.',
                              )
                            : gettext(
                                  'Join Career Accelerators to get the structure, skills, and real-world experience that makes you an exceptional candidate.',
                              )}
                    </div>
                </div>

                <InDemandCareerWrapper
                    headingProps={{children: []}}
                    buttonProps={{className: styles['hide-button']}}
                    academies={academies}
                    isUSMarketplace={isUSMarketplace}
                    uiRegion={'all_academies_careers_exploration'}
                    className={styles['in-demand-career-container']}
                />
            </AboveTheFoldProvider>

            {isUSMarketplace && (
                <>
                    <div
                        className={classNames(
                            'ud-full-viewport-container',
                            styles['testimonials-wrapper'],
                        )}
                    >
                        <div
                            className={classNames('ud-container', styles['testimonials-container'])}
                        >
                            <h2
                                className={classNames(
                                    'ud-heading-xl',
                                    styles['testimonials-heading'],
                                )}
                            >
                                {gettext('What other learners are saying')}
                            </h2>
                            <div className={styles['testimonials-grid']}>
                                {OVERVIEW_TESTIMONIALS.map((testimonial) => (
                                    <LearningTestimonialCard
                                        key={testimonial.id}
                                        id={testimonial.id}
                                        content={testimonial.content}
                                        relatedUrl={testimonial.relatedUrl}
                                        relatedUrlText={testimonial.relatedUrlText}
                                        source={testimonial.source}
                                        type={testimonial.type}
                                        size="large"
                                        className={styles['testimonial-card']}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                    <div
                        className={classNames(
                            'ud-full-viewport-container',
                            styles['articles-container'],
                        )}
                    >
                        <AdditionalResources articles={articles} />
                    </div>
                </>
            )}

            <div className={classNames('ud-full-viewport-container', styles['faq-container'])}>
                <FrequentlyAskedQuestions questionsAndAnswers={OVERVIEW_FAQS} />
                <ContentArea className={classNames('ud-container', styles['area'])}>
                    <ContentGrid>
                        {isUSMarketplace && (
                            <ContentGridItem defaultColumn={'1 / span 4'} mdColumn={'2 / span 10'}>
                                <div className={classNames('ud-text-xs', styles['disclaimer'])}>
                                    <Disclaimer />
                                </div>
                            </ContentGridItem>
                        )}
                    </ContentGrid>
                </ContentArea>
            </div>
        </div>
    );
}
